/**
 * Document Preview Service
 * Handles document preview functionality including SAS token retrieval
 * and document download for preview purposes
 */

import { acquireDelegatedGraphToken } from '../utils/msalTokenService';
import { authenticatedFetch } from '../utils/authErrorHandler';

interface UserDocument {
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  originalFilename?: string;
}

interface SASData {
  sasUrl?: string;
  filePath?: string;
  expiresAt?: string | null;
  permissions?: string;
  isSharePoint?: boolean;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  source?: string;
}

interface DocumentPreviewResult {
  sasData: SASData;
  contentBlob: Blob;
  document: UserDocument;
}

interface ExtendedError extends Error {
  code?: string;
}

// Progress callback for tracking download progress
export type DownloadProgressCallback = (progress: {
  loaded: number;
  total: number | null;
  percentage: number | null;
}) => void;

// Get base URL for file access service
const getFileAccessBaseUrl = (): string => {
  // Check for specific file access service URL first
  const fileAccessUrl = import.meta.env.VITE_FILE_ACCESS_API_BASE_URL;
  if (fileAccessUrl) {
    return fileAccessUrl;
  }

  // Fallback to deals API base URL (assuming file access is on the same VM)
  const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL || 'https://default-api-url.com';
  return baseUrl;
};

// getAuthHeaders removed — now using authenticatedFetch from authErrorHandler

// Office formats that Microsoft Graph can convert to PDF via ?format=pdf
const GRAPH_CONVERTIBLE_EXTENSIONS: string[] = [
  '.pptx', '.ppt', '.docx', '.doc', '.xlsx', '.xls'
];

/**
 * Check if a file path is a direct URL
 */
const isDirectUrl = (filePath: string): boolean => {
  return !!filePath && (filePath.startsWith('http://') || filePath.startsWith('https://'));
};

/**
 * Check if a file path is a SharePoint URL
 */
const isSharePointUrl = (filePath: string): boolean => {
  return !!filePath && filePath.includes('sharepoint.com');
};

/**
 * Check if a document has SharePoint identifiers
 */
const hasSharePointIdentifiers = (userDocument: UserDocument): boolean => {
  return !!(userDocument?.sharepointSiteId && userDocument?.sharepointDriveId && userDocument?.sharepointItemId);
};

/**
 * Download document content from SharePoint using Microsoft Graph API
 */
const downloadFromSharePoint = async (userDocument: UserDocument): Promise<Blob> => {
  const { sharepointSiteId, sharepointDriveId, sharepointItemId, originalFilename } = userDocument;

  // Guard: Ensure all identifiers are present
  if (!sharepointSiteId || !sharepointDriveId || !sharepointItemId) {
    console.error('❌ [DocumentPreviewService] Missing SharePoint identifiers:', {
      hasSiteId: !!sharepointSiteId,
      hasDriveId: !!sharepointDriveId,
      hasItemId: !!sharepointItemId
    });
    const error: ExtendedError = new Error('Missing SharePoint identifiers - please refresh the project context');
    error.code = 'MISSING_IDENTIFIERS';
    throw error;
  }

  try {
    // Acquire delegated Graph token (reusing existing Files.Read.All consent from Phase 1)
    const graphToken: string = await acquireDelegatedGraphToken();

    // Build Graph API URL for document content
    // For PDFs: fetch native content (Graph returns 406 if you request ?format=pdf on a PDF)
    // For Office formats (pptx, docx, xlsx, etc.): append ?format=pdf for server-side conversion
    const isPdf: boolean = !!originalFilename && originalFilename.toLowerCase().endsWith('.pdf');
    const graphUrl: string = `https://graph.microsoft.com/v1.0/sites/${sharepointSiteId}/drives/${sharepointDriveId}/items/${sharepointItemId}/content${isPdf ? '' : '?format=pdf'}`;

    // Fetch document content with delegated token
    const response: Response = await fetch(graphUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${graphToken}`,
        'Accept': 'application/pdf, application/octet-stream'
      }
    });

    // Handle permission errors specifically
    if (response.status === 401 || response.status === 403) {
      console.error('❌ [DocumentPreviewService] SharePoint access denied (403/401)');
      const error: ExtendedError = new Error('SharePoint access denied - your permissions may have been revoked');
      error.code = 'SHAREPOINT_DENIED';
      throw error;
    }

    // Handle other HTTP errors
    if (!response.ok) {
      const errorText: string = await response.text();
      console.error('❌ [DocumentPreviewService] Graph API request failed:', response.status, errorText);
      throw new Error(`Failed to fetch from SharePoint: ${response.status} ${response.statusText}`);
    }

    // Convert response to blob
    const blob: Blob = await response.blob();
    return blob;
  } catch (error) {
    // Re-throw structured errors (SHAREPOINT_DENIED, MISSING_IDENTIFIERS)
    if ((error as ExtendedError).code) {
      throw error;
    }
    // Wrap other errors
    console.error('❌ [DocumentPreviewService] SharePoint download failed:', error);
    throw new Error(`SharePoint download error: ${(error as Error).message}`);
  }
};

/**
 * Download document content from SharePoint with progress tracking
 */
const downloadFromSharePointWithProgress = async (
  userDocument: UserDocument,
  onProgress?: DownloadProgressCallback
): Promise<Blob> => {
  const { sharepointSiteId, sharepointDriveId, sharepointItemId, originalFilename } = userDocument;

  if (!sharepointSiteId || !sharepointDriveId || !sharepointItemId) {
    const error: ExtendedError = new Error('Missing SharePoint identifiers - please refresh the project context');
    error.code = 'MISSING_IDENTIFIERS';
    throw error;
  }

  try {
    const graphToken: string = await acquireDelegatedGraphToken();
    const isPdf: boolean = !!originalFilename && originalFilename.toLowerCase().endsWith('.pdf');
    const graphUrl: string = `https://graph.microsoft.com/v1.0/sites/${sharepointSiteId}/drives/${sharepointDriveId}/items/${sharepointItemId}/content${isPdf ? '' : '?format=pdf'}`;

    const response: Response = await fetch(graphUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${graphToken}`,
        'Accept': 'application/pdf, application/octet-stream'
      }
    });

    if (response.status === 401 || response.status === 403) {
      const error: ExtendedError = new Error('SharePoint access denied - your permissions may have been revoked');
      error.code = 'SHAREPOINT_DENIED';
      throw error;
    }

    if (!response.ok) {
      const errorText: string = await response.text();
      throw new Error(`Failed to fetch from SharePoint: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Get content length for progress calculation
    const contentLength = response.headers.get('Content-Length');
    const total = contentLength ? parseInt(contentLength, 10) : null;

    // If no body or no progress callback, fall back to simple blob()
    if (!response.body || !onProgress) {
      const blob = await response.blob();
      return blob;
    }

    // Stream the response to track progress
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      loaded += value.length;

      // Report progress
      onProgress({
        loaded,
        total,
        percentage: total ? Math.round((loaded / total) * 100) : null
      });
    }

    // Combine chunks into a single Uint8Array, then create blob
    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    const blob = new Blob([combined], { type: 'application/pdf' });
    return blob;

  } catch (error) {
    if ((error as ExtendedError).code) {
      throw error;
    }
    console.error('❌ [DocumentPreviewService] SharePoint download with progress failed:', error);
    throw new Error(`SharePoint download error: ${(error as Error).message}`);
  }
};

/**
 * Get SAS token for downloading a user document
 */
export const getDocumentDownloadSAS = async (
  userDocument: UserDocument,
  _projectId?: string,
  _username?: string
): Promise<SASData> => {
  try {
    // Priority 1: Check for SharePoint identifiers (Phase 3: Graph API path)
    if (hasSharePointIdentifiers(userDocument)) {
      // Return a marker object indicating this is a SharePoint document
      // The getDocumentPreview function will handle the actual download
      return {
        isSharePoint: true,
        sharepointSiteId: userDocument.sharepointSiteId,
        sharepointDriveId: userDocument.sharepointDriveId,
        sharepointItemId: userDocument.sharepointItemId
      };
    }

    // Priority 2: Extract filePath from document (Phase 4: filePath only contract)
    const filePath: string | undefined = userDocument.filePath;

    if (!filePath) {
      throw new Error('Document missing required filePath field');
    }

    // If filePath is a SharePoint URL but no identifiers, throw error
    // (This shouldn't happen if backend is consistent, but guard against it)
    if (isSharePointUrl(filePath)) {
      console.error('📎 [DocumentPreviewService] filePath is SharePoint URL but missing identifiers');
      const error: ExtendedError = new Error('SharePoint document missing identifiers - please refresh project context');
      error.code = 'MISSING_IDENTIFIERS';
      throw error;
    }

    // If filePath is a direct URL (starts with http:// or https://), return it directly
    if (isDirectUrl(filePath)) {
      return {
        sasUrl: filePath,
        filePath: filePath,
        expiresAt: null,
        permissions: 'r'
      };
    }

    // Otherwise, it's a blob storage path - get SAS token from file-access service
    const baseUrl: string = getFileAccessBaseUrl();

    // Phase 4 contract: single filePath parameter
    // Backend expects snake_case 'file_path' and returns camelCase response
    const requestBody: { file_path: string } = {
      file_path: filePath
    };

    const response: Response = await authenticatedFetch(`${baseUrl}/api/file-access/sas/download`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText: string = await response.text();
      throw new Error(`Failed to get download SAS: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const sasData: SASData = await response.json();
    return sasData;
  } catch (error) {
    throw error;
  }
};

/**
 * Download document content using SAS URL
 */
export const downloadDocumentContent = async (sasUrl: string): Promise<Blob> => {
  try {
    const response: Response = await fetch(sasUrl, {
      method: 'GET',
      cache: 'no-store',  // Prevent browser caching (fixes Edge refresh issues)
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
    }

    const blob: Blob = await response.blob();
    return blob;
  } catch (error) {
    throw error;
  }
};

/**
 * Get document preview data (SAS token + content)
 */
export const getDocumentPreview = async (
  userDocument: UserDocument,
  projectId?: string,
  username?: string
): Promise<DocumentPreviewResult> => {
  try {
    // First get the SAS token (or SharePoint marker)
    const sasData: SASData = await getDocumentDownloadSAS(userDocument, projectId, username);

    // Check if this is a SharePoint document
    if (sasData.isSharePoint) {
      // Download directly from SharePoint using Graph API
      const contentBlob: Blob = await downloadFromSharePoint(userDocument);

      return {
        sasData: {
          ...sasData,
          source: 'sharepoint'
        },
        contentBlob,
        document: userDocument
      };
    }

    // Otherwise, download using SAS URL
    // Backend returns camelCase 'sasUrl' (Phase 4: no fallbacks)
    const contentBlob: Blob = await downloadDocumentContent(sasData.sasUrl!);

    return {
      sasData,
      contentBlob,
      document: userDocument
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get document preview data with progress tracking (for SharePoint documents)
 */
export const getDocumentPreviewWithProgress = async (
  userDocument: UserDocument,
  projectId?: string,
  username?: string,
  onProgress?: DownloadProgressCallback
): Promise<DocumentPreviewResult> => {
  try {
    const sasData: SASData = await getDocumentDownloadSAS(userDocument, projectId, username);

    if (sasData.isSharePoint) {
      // Download from SharePoint with progress tracking
      const contentBlob: Blob = await downloadFromSharePointWithProgress(userDocument, onProgress);

      return {
        sasData: {
          ...sasData,
          source: 'sharepoint'
        },
        contentBlob,
        document: userDocument
      };
    }

    // For non-SharePoint, use regular download (no progress needed - we use URL directly for PDFs)
    const contentBlob: Blob = await downloadDocumentContent(sasData.sasUrl!);

    return {
      sasData,
      contentBlob,
      document: userDocument
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Determine if a document can be previewed
 */
export const canPreviewDocument = (userDocument: UserDocument): boolean => {
  if (!userDocument) {
    return false;
  }

  const filename: string | undefined = userDocument.originalFilename;
  if (!filename) {
    return false;
  }

  const filenameLower: string = filename.toLowerCase();
  const supportedExtensions: string[] = ['.pdf', '.md', '.markdown', '.txt', '.yaml', '.yml'];

  if (supportedExtensions.some((ext: string) => filenameLower.endsWith(ext))) {
    return true;
  }

  // Office formats are previewable via Graph API PDF conversion when SharePoint IDs are available
  if (GRAPH_CONVERTIBLE_EXTENSIONS.some((ext: string) => filenameLower.endsWith(ext))) {
    return hasSharePointIdentifiers(userDocument);
  }

  return false;
};

/**
 * Get document type for preview rendering
 */
export const getDocumentPreviewType = (userDocument: UserDocument): string => {
  if (!userDocument) {
    return 'unknown';
  }

  const filename: string | undefined = userDocument.originalFilename;
  if (!filename) {
    return 'unknown';
  }

  const filenameLower: string = filename.toLowerCase();

  if (filenameLower.endsWith('.pdf')) {
    return 'pdf';
  } else if (GRAPH_CONVERTIBLE_EXTENSIONS.some((ext: string) => filenameLower.endsWith(ext))) {
    return 'pdf';
  } else if (filenameLower.endsWith('.md') || filenameLower.endsWith('.markdown')) {
    return 'markdown';
  } else if (filenameLower.endsWith('.txt')) {
    return 'text';
  } else if (filenameLower === 'draft_metadata.yaml') {
    return 'draft_metadata';
  } else if (filenameLower === 'draft_content.yaml') {
    return 'draft_content';
  } else if (filenameLower === 'draft_outline.yaml') {
    return 'draft_outline';
  } else if (filenameLower.endsWith('.yaml') || filenameLower.endsWith('.yml')) {
    return 'yaml';
  }

  return 'unknown';
};
